import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, Send, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useLanguage } from '@/contexts/LanguageContext'

interface PublishedStrategy {
  id: number
  title: string
  map_name: string
  map_label: string
  hero: string
  url: string
  slides: number
  created_by_name: string
}

interface LinkedStrategy { id: number; title: string; map_name: string }

interface StrategyState {
  published: PublishedStrategy | null
  /** Staff only: strategies linked to this match in the library. */
  linked?: LinkedStrategy[]
}

/**
 * Step 3 of a match, next to the sign-up and position cards.
 *
 * Members see the strategy once it is published. Staff pick one of the
 * strategies linked to the match and publish it — that fills message 3 of the
 * match channel and notifies the roster. Nothing renders for a member while
 * there is nothing published: a card that only says "not yet" is noise.
 */
export default function MatchStrategyCard({ eventId, canPublish, bare = false, onChange }: {
  eventId: string
  canPublish: boolean
  /** Inside the match page's step panel: no card frame, no title of its own. */
  bare?: boolean
  /** After a publish or unpublish, so the match list's step track catches up. */
  onChange?: () => void
}) {
  const { T } = useLanguage()
  const [state, setState] = useState<StrategyState | null>(null)
  const [choice, setChoice] = useState<number | ''>('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  const load = useCallback(() => {
    fetch(`/api/matches/${eventId}/strategy`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then((d: StrategyState | null) => {
        setState(d)
        if (d) setChoice(d.published?.id ?? d.linked?.[0]?.id ?? '')
      })
      .catch(() => setState(null))
  }, [eventId])

  useEffect(() => { setMessage(null); load() }, [load])

  if (!state) return null
  const published = state.published
  if (!published && !canPublish && !bare) return null

  const send = async (method: 'POST' | 'DELETE') => {
    setBusy(true)
    setMessage(null)
    try {
      const res = await fetch(`/api/matches/${eventId}/strategy`, {
        method,
        credentials: 'include',
        headers: method === 'POST' ? { 'Content-Type': 'application/json' } : undefined,
        body: method === 'POST' ? JSON.stringify({ strategy_id: choice }) : undefined,
      })
      const d = await res.json().catch(() => ({}))
      if (!res.ok) {
        setMessage({ ok: false, text: d.detail ?? T.matches.strategyError })
        return
      }
      setMessage({
        ok: true,
        text: method === 'DELETE'
          ? T.matches.strategyUnpublished
          : d.notified
            ? T.matches.strategyPublishedNotified.replace('{n}', String(d.notified))
            : T.matches.strategyPublished,
      })
      load()
      onChange?.()
    } catch {
      setMessage({ ok: false, text: T.matches.strategyError })
    } finally {
      setBusy(false)
    }
  }

  const linked = state.linked ?? []
  const sameAsPublished = published && choice === published.id

  const body = (
    <>
        {published ? (
          <Link
            to={published.url}
            className="group block overflow-hidden rounded-md border border-white/10 transition-colors hover:border-white/25"
          >
            {published.hero && (
              <div className="relative aspect-[16/7] overflow-hidden bg-black/40">
                <img src={published.hero} alt="" className="h-full w-full object-cover" style={{ objectPosition: 'center 32%' }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              </div>
            )}
            <div className="flex items-center gap-2 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{published.title}</p>
                <p className="truncate text-[11px] text-muted-foreground">
                  {published.map_label}
                  {published.slides > 0 && ` · ${T.matches.strategySlides.replace('{n}', String(published.slides))}`}
                </p>
              </div>
              <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50 group-hover:text-foreground" />
            </div>
          </Link>
        ) : (
          <p className="text-sm text-muted-foreground/50 italic">{T.matches.strategyNotPublished}</p>
        )}

        {canPublish && (
          linked.length === 0 ? (
            <p className="text-[11px] leading-relaxed text-muted-foreground/60">
              {T.matches.strategyNoneLinked}{' '}
              <Link to="/strategy/mac" className="text-foreground/80 underline underline-offset-2 hover:text-foreground">
                {T.matches.strategyOpenLibrary}
              </Link>
            </p>
          ) : (
            <div className="space-y-2 border-t border-white/8 pt-3">
              <select
                value={choice}
                onChange={e => setChoice(e.target.value ? Number(e.target.value) : '')}
                className="h-8 w-full cursor-pointer rounded-sm border border-white/10 bg-card px-2 text-sm focus:border-primary focus:outline-none"
              >
                {linked.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
              </select>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" className="h-7 gap-1.5 text-xs" disabled={busy || choice === ''} onClick={() => send('POST')}>
                  <Send className="h-3 w-3" />
                  {sameAsPublished ? T.matches.strategyUpdate : T.matches.strategyPublish}
                </Button>
                {published && (
                  <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-muted-foreground" disabled={busy} onClick={() => send('DELETE')}>
                    <X className="h-3 w-3" />{T.matches.strategyUnpublish}
                  </Button>
                )}
              </div>
              {!published && <p className="text-[11px] text-muted-foreground/50">{T.matches.strategyPublishHint}</p>}
            </div>
          )
        )}

        {message && (
          <p className={`text-[11px] ${message.ok ? 'text-green-400/80' : 'text-destructive'}`}>{message.text}</p>
        )}
    </>
  )

  if (bare) return <div className="space-y-3">{body}</div>
  return (
    <div className="max-w-sm flex-1 min-w-[260px]">
      <div className="bg-white/3 border border-white/8 rounded-lg px-4 py-3 space-y-3 h-full">
        <p className="text-base font-bold tracking-wide text-foreground">{T.matches.strategyTitle}</p>
        {body}
      </div>
    </div>
  )
}
